import { useMemo } from "react";
import { SalonReport } from "../../../../types/reports";
import { ReportV2Definition, ViewConfig } from "../../../../types/reportsV2";
import {
  normalizeViewConfig,
  viewsEqual,
  withValidSort,
} from "../model/viewConfig";
import {
  useViewDraft,
  ViewDraft,
  ViewPatch as GenericViewPatch,
  ViewRules,
} from "./useViewDraft";

export type ViewPatch = GenericViewPatch<ViewConfig>;

export interface UseReportDraftOptions {
  report: SalonReport;
  definition: ReportV2Definition;
  /** Report display name (unsaved-changes dialog title). */
  name: string;
  /** PUT the view as the report's saved view; resolves with the updated
   *  report (the caller puts it back into the report list). */
  persistView: (view: ViewConfig) => Promise<SalonReport>;
  /** Sidebar unsaved dot. Called with false on unmount. */
  onDirtyChange?: (dirty: boolean) => void;
}

export type ReportDraft = ViewDraft<ViewConfig>;

/**
 * Draft vs saved view for a v2 *table* report (useViewDraft with the table
 * view rules). Every edit keeps the sort valid: hiding the sorted column falls
 * back to the default order. (Duplicate moves an unsaved draft onto the copy
 * through the shell's draft source; its `discard` resets this report
 * afterwards.)
 */
export function useReportDraft({
  definition,
  ...options
}: UseReportDraftOptions): ReportDraft {
  const rules = useMemo<ViewRules<ViewConfig>>(
    () => ({
      normalize: (stored) => normalizeViewConfig(definition, stored),
      equal: viewsEqual,
      sanitize: withValidSort,
    }),
    [definition],
  );
  return useViewDraft({ ...options, rules });
}
